import { getFormContext } from "config/FormContext";
import React, { useContext, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { InputCostumeErrorType, InputErrorType, InputExtensionType } from "types/form-types";
import { validateInput } from "utilities/input-utilities";
import { getTranslationText } from "utilities/translation-utilities"
import '../Form.scss';
import InputExtension from "./input-common/InputExtension"
import InputWrapper from "./input-common/InputWrapper"
import { hasValidNumberValue } from "utilities/app-utilities";

export type BaseInputPropsType<THTMLElement = HTMLInputElement, TDataType = string | number> = {
    customErrors?: InputCostumeErrorType<TDataType, THTMLElement>[]
    name?: string,
    id?: string
    autofill?: string
    label?: string | React.ReactNode
    required?: boolean
    autoFocus?: boolean
    readonly?: boolean
    disabled?: boolean,
    value?: TDataType,
    onFocus?: (input: React.ChangeEvent<THTMLElement>) => void
    touched?: boolean
    placeholder?: string,
    hideErrorMessages?: boolean,
    description?: string | React.ReactNode
    onChange?: (s: TDataType | undefined, input: React.ChangeEvent<THTMLElement>) => void
    onBlur?: (s: TDataType | undefined, input: React.ChangeEvent<THTMLElement>) => void,
    prefix?: InputExtensionType | InputExtensionType[],
    suffix?: InputExtensionType | InputExtensionType[]
}

export type InputPropsType = BaseInputPropsType<HTMLInputElement, string> & {
    type?: 'text' | 'password' | 'search' | 'email' | 'username' | 'number' | 'tel' | 'date' | 'color',
    minLength?: number
    maxLength?: number,
    required?: boolean
    pattern?: string,
    selectTextOnFocus?: boolean,
    min?: number
    max?: number,
    wrapperClassName?: string,
    step?: number,
    size?: number,
    onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void
}

const Input = (props: InputPropsType) => {
    const formContext = getFormContext(useContext);
    const [t] = useTranslation();
    const input = useRef<HTMLInputElement>(null);
    const inputWrapper = useRef<HTMLDivElement>(null);
    let [touched, setTouched] = useState(props.touched || formContext.submitted ? true : false);
    let [errors, setErrors] = useState<InputErrorType[]>([]);
    let [isFocused, setIsFocused] = useState<boolean>(false);
    
    const placeholder = getTranslationText(props.placeholder, t);

    useEffect(() => {
        validate(true);
        if(props.autoFocus && input.current) {
            input.current.focus();
        }
    }, [])

    useEffect(() => {
        setTouched(formContext.submitted);
        validate(!formContext.submitted);
    }, [formContext.submitted])

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (typeof props.onChange == 'function') {
            props.onChange(e.currentTarget.value, e);
        }

        if (touched) {
            validate();
        }
    };

    const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setTouched(true);
        validate();
        if(typeof props.onBlur == 'function') {
            props.onBlur(e.currentTarget.value, e);
        }
        setIsFocused(false);
    }

    const errorValidations = () => {
        const errorValidators = [...props.customErrors || []];
        if(hasValidNumberValue(props.max)) {
            errorValidators.push({
                message: t('form.errors.rangeOverflow', {max: props.max}),
                name: 'rangeOverflow',
                validate(value) {
                    return value && Number(value) > props.max! ? false : true;
                },
            });
        }

        if(hasValidNumberValue(props.min)) {
            errorValidators.push({
                message: t('form.errors.rangeUnderflow', {min: props.min}),
                name: 'rangeUnderflow',
                validate(value) {
                    return value && Number(value) < props.min! ? false : true;
                },
            });
        }

        return errorValidators;
    }

    function validate(soft?: boolean) {
        setErrors(validateInput({
            value: props.value,
            input: input?.current || undefined,
            inputWrapper: inputWrapper?.current || undefined,
            customErrors: errorValidations(),
            soft: soft ? true : false
        }) || []);
    }

    return (
        <InputWrapper
            errors={errors}
            inputWrapperRef={inputWrapper}
            label={props.label}
            wrapperClassName={props.wrapperClassName}
            required={props.required}
            hideErrorMessages={props.hideErrorMessages}
        >
            <InputExtension icons={props.prefix} isFocused={isFocused} />
            <input
                ref={input}
                type={props.type || 'text'}
                name={props.name}
                minLength={props.minLength}
                maxLength={props.maxLength}
                required={props.required}
                size={props.size || 50}
                className={`${props.prefix ? '' : 'pl'} ${props.suffix ? '' : 'pr'}`.trim()}
                autoComplete="none"
                disabled={props.disabled}
                readOnly={props.readonly}
                onKeyUp={props.onKeyPress}
                value={props.value as any}
                onFocus={v => {
                    setIsFocused(true);
                    if (props.selectTextOnFocus) v.target.select();
                    if (props.onFocus) props.onFocus(v);
                }}
                pattern={props.pattern}
                onChange={onChange}
                onBlur={e => onBlur(e)}
                placeholder={placeholder || ''}
                step={props.step}
            />
            <InputExtension icons={props.suffix} isFocused={isFocused} />
        </InputWrapper>
    );
};
export default Input;
